import type { ReactNode } from 'react';

interface SectionHeaderProps {
  /**
   * `id` for the `h2`. The owning `<section>` points its `aria-labelledby` here, so the
   * section is named by the heading a sighted reader sees rather than by a duplicate label.
   */
  readonly id: string;
  readonly title: string;
  /**
   * Optional right-aligned note. Omit it — or pass `null` — to render the rule alone.
   * Accepts nodes, not just text: the prototype's notes emphasise the role word in `<b>`.
   */
  readonly note?: ReactNode;
}

/**
 * The Help & FAQ section header: a heading, a hairline rule that absorbs whatever width the
 * heading and note leave, and an optional right-aligned note.
 *
 * Shared rather than repeated because every section on the page uses it — see
 * `design_handoff_help_faq/README.md`, which introduces it as the "section header pattern,
 * used by every section on the page". Styling lives in `../help-faq.css`.
 *
 * Lives under `pages/help/` alongside the sections that consume it (`FaqAccordion`,
 * `Doors`, `PageFooterRows`) rather than being exported from `HelpPage.tsx`, which those
 * sections would then have to import back from their own parent.
 */
export function SectionHeader({ id, title, note }: SectionHeaderProps) {
  return (
    <div className="help-sec-head">
      <h2 id={id}>{title}</h2>
      {/* A decorative rule, so an empty div is right: it is absent from the accessibility
          tree, where an <hr> would announce a separator that carries no meaning here.
          `aria-hidden` is belt-and-braces — an empty div is already ignored, but this keeps
          it ignored if the element ever gains content. */}
      <div className="help-sec-rule" aria-hidden="true" />
      {note == null ? null : <div className="help-sec-note">{note}</div>}
    </div>
  );
}
